import { useEffect, useState } from 'react'
import { ShieldCheck, CheckCircle2, XCircle, History } from 'lucide-react'
import api from '../services/api'
import { useEmpresa } from '../contexts/EmpresaContext'

// Ordens de Compra aguardando aprovação (WCco504 do ERP) — aprovar grava pecits.ppc_status = 'L' (igual à PCco021c),
// reprovar grava 'R'. Dá pra trocar de aprovada pra reprovada e vice-versa; o histórico guarda quem, quando e o motivo.

interface Ordem {
  est: number; numero: number; fantasia: string; fornecedor: string; dtemis: string; comprador: string | null
  qtetotal: string; vltotal: string; aprovacao: 'A' | 'L' | 'R'; aprovacaoLabel: string
}
interface Hist { status: string; rotulo: string; usuario: string; data_hora: string; observacao: string | null }

const COR: Record<string, { cor: string; fundo: string }> = {
  A: { cor: '#b45309', fundo: '#fff7ed' }, L: { cor: '#15803d', fundo: '#f0fdf4' }, R: { cor: '#b91c1c', fundo: '#fef2f2' },
}
const br = (iso: string) => (iso ? iso.slice(0, 10).split('-').reverse().join('/') : '')
const moeda = (v: string | number) => Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const isoLocal = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

export default function AprovacaoOrdemCompra() {
  const { empresaCod } = useEmpresa()
  const hoje = new Date()
  const [status, setStatus] = useState<'A' | 'L' | 'R'>('A')
  const [dataDe, setDataDe] = useState(isoLocal(new Date(hoje.getFullYear(), hoje.getMonth() - 3, 1)))
  const [dataAte, setDataAte] = useState(isoLocal(hoje))
  const [lista, setLista] = useState<Ordem[] | null>(null)
  const [erro, setErro] = useState('')
  const [aviso, setAviso] = useState('')
  const [decidindo, setDecidindo] = useState<{ ordem: Ordem; status: 'L' | 'R' } | null>(null)
  const [obs, setObs] = useState('')
  const [gravando, setGravando] = useState(false)
  const [historico, setHistorico] = useState<{ ordem: Ordem; itens: Hist[] } | null>(null)

  async function carregar() {
    setErro('')
    try {
      const r = await api.get('/erp/ordem-compra-aprovacao/lista', { params: { empcodDe: empresaCod ?? 1, empcodAte: empresaCod ?? 1, dataDe, dataAte, aprovacao: status } })
      setLista(r.data)
    } catch (e: any) {
      setErro(e?.response?.data?.error || 'Erro ao carregar as ordens.')
    }
  }
  useEffect(() => { carregar() }, [status, empresaCod]) // eslint-disable-line react-hooks/exhaustive-deps

  async function confirmar() {
    if (!decidindo) return
    if (decidindo.status === 'R' && !obs.trim()) { setErro('Informe o motivo da reprovação.'); return }
    setGravando(true); setErro('')
    try {
      await api.post(`/erp/ordem-compra-aprovacao/${decidindo.ordem.est}/${decidindo.ordem.numero}`, { status: decidindo.status, observacao: obs })
      setAviso(`Ordem ${decidindo.ordem.numero} ${decidindo.status === 'L' ? 'aprovada' : 'reprovada'}.`)
      setDecidindo(null); setObs('')
      await carregar()
    } catch (e: any) {
      setErro(e?.response?.data?.error || 'Erro ao gravar a decisão.')
    } finally {
      setGravando(false)
    }
  }

  async function verHistorico(o: Ordem) {
    try { const r = await api.get(`/erp/ordem-compra-aprovacao/${o.est}/${o.numero}/historico`); setHistorico({ ordem: o, itens: r.data }) }
    catch { setErro('Erro ao carregar o histórico.') }
  }

  const th: React.CSSProperties = { padding: '0.5rem 0.6rem', fontSize: '0.68rem', fontWeight: 800, color: '#fff', background: '#1b3f7b', textAlign: 'left', whiteSpace: 'nowrap', textTransform: 'uppercase' }
  const td: React.CSSProperties = { padding: '0.45rem 0.6rem', fontSize: '0.82rem', borderBottom: '1px solid #eef2f7', whiteSpace: 'nowrap' }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', maxWidth: 1200 }}>
      <div className="modal-box" style={{ width: '100%', maxWidth: 'none', maxHeight: 'none' }}>
        <div className="modal-header" style={{ background: 'linear-gradient(135deg, #1a6ea8, #14547f)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.7rem' }}>
            <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 34, height: 34, borderRadius: 9, background: 'rgba(255,255,255,0.15)' }}><ShieldCheck size={18} color="#fff" /></span>
            <div>
              <h2>Ordens de Compra — Aprovação</h2>
              <div style={{ fontSize: '0.7rem', color: '#dbeafe', marginTop: 2 }}>Aprovar ou reprovar ordens (parâmetro "Exige liberação Ordem de Compra")</div>
            </div>
          </div>
          <span style={{ fontSize: '0.78rem', fontWeight: 700, color: 'rgba(219,234,254,0.8)' }}>WCCO504</span>
        </div>
        <div className="modal-corpo" style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: 4 }}>
            {(['A', 'L', 'R'] as const).map(s => (
              <button key={s} className="btn btn-sm" onClick={() => setStatus(s)}
                style={{ border: `1px solid ${COR[s].cor}`, background: status === s ? COR[s].cor : COR[s].fundo, color: status === s ? '#fff' : COR[s].cor, fontWeight: 700 }}>
                {{ A: 'Aguardando análise', L: 'Aprovadas', R: 'Reprovadas' }[s]}
              </button>
            ))}
          </div>
          <div className="field" style={{ margin: 0 }}><label>Emissão de</label><input type="date" value={dataDe} onChange={e => setDataDe(e.target.value)} /></div>
          <div className="field" style={{ margin: 0 }}><label>até</label><input type="date" value={dataAte} onChange={e => setDataAte(e.target.value)} /></div>
          <button className="btn btn-primary" onClick={carregar}>Pesquisar</button>
        </div>
      </div>

      {erro && <div style={{ background: '#fee2e2', color: '#991b1b', border: '1px solid #fecaca', borderRadius: 8, padding: '0.55rem 0.9rem', fontSize: '0.82rem', fontWeight: 600 }}>{erro}</div>}
      {aviso && <div style={{ background: '#f0fdf4', color: '#15803d', border: '1px solid #bbf7d0', borderRadius: 8, padding: '0.55rem 0.9rem', fontSize: '0.82rem', fontWeight: 600 }}>{aviso}</div>}

      <div style={{ overflowX: 'auto', border: '1px solid #bfdbfe', borderRadius: 8, background: '#fff' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={th}>Est</th><th style={th}>Número</th><th style={th}>Emissão</th><th style={th}>Fornecedor</th><th style={th}>Comprador</th>
              <th style={{ ...th, textAlign: 'right' }}>Valor</th><th style={th}>Status</th><th style={{ ...th, textAlign: 'center' }}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {lista && lista.length === 0 && <tr><td colSpan={8} style={{ ...td, textAlign: 'center', color: '#94a3b8', padding: '1.2rem' }}>Nenhuma ordem nessa situação.</td></tr>}
            {lista?.map((o, i) => (
              <tr key={`${o.est}-${o.numero}`} style={{ background: i % 2 ? '#f5f9fd' : '#fff' }}>
                <td style={td}>{o.est}</td>
                <td style={{ ...td, fontWeight: 800 }}>{o.numero}</td>
                <td style={td}>{br(o.dtemis)}</td>
                <td style={{ ...td, whiteSpace: 'normal' }}>{o.fantasia || o.fornecedor}</td>
                <td style={td}>{o.comprador || '—'}</td>
                <td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>{moeda(o.vltotal)}</td>
                <td style={td}><span style={{ fontSize: '0.72rem', fontWeight: 800, borderRadius: 999, padding: '2px 9px', color: COR[o.aprovacao].cor, background: COR[o.aprovacao].fundo }}>{o.aprovacaoLabel}</span></td>
                <td style={{ ...td, textAlign: 'center' }}>
                  <div style={{ display: 'inline-flex', gap: 5 }}>
                    {o.aprovacao !== 'L' && <button className="btn btn-sm" style={{ background: '#15803d', color: '#fff', display: 'inline-flex', alignItems: 'center', gap: 4 }} onClick={() => { setDecidindo({ ordem: o, status: 'L' }); setObs(''); setAviso('') }}><CheckCircle2 size={14} /> Aprovar</button>}
                    {o.aprovacao !== 'R' && <button className="btn btn-sm" style={{ background: '#fff', color: '#b91c1c', border: '1px solid #fecaca', display: 'inline-flex', alignItems: 'center', gap: 4 }} onClick={() => { setDecidindo({ ordem: o, status: 'R' }); setObs(''); setAviso('') }}><XCircle size={14} /> Reprovar</button>}
                    <button className="btn btn-sm btn-outline" title="Histórico" onClick={() => verHistorico(o)}><History size={14} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {decidindo && (
        <div className="modal-overlay" style={{ zIndex: 1300 }} onClick={e => e.target === e.currentTarget && setDecidindo(null)}>
          <div className="modal-box" style={{ maxWidth: 480 }}>
            <div className="modal-header" style={{ background: decidindo.status === 'L' ? '#15803d' : '#b91c1c' }}>
              <h2>{decidindo.status === 'L' ? 'Aprovar' : 'Reprovar'} ordem {decidindo.ordem.numero}</h2>
              <button className="modal-fechar" onClick={() => setDecidindo(null)}>✕</button>
            </div>
            <div className="modal-corpo">
              <p style={{ margin: '0 0 0.6rem', fontSize: '0.85rem' }}>{decidindo.ordem.fantasia || decidindo.ordem.fornecedor} · R$ {moeda(decidindo.ordem.vltotal)}</p>
              <div className="field">
                <label>{decidindo.status === 'R' ? 'Motivo da reprovação *' : 'Observação (opcional)'}</label>
                <textarea value={obs} maxLength={300} rows={3} onChange={e => setObs(e.target.value)} style={{ width: '100%', boxSizing: 'border-box', resize: 'vertical' }} autoFocus />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setDecidindo(null)}>Cancelar</button>
              <button className="btn" disabled={gravando} onClick={confirmar} style={{ background: decidindo.status === 'L' ? '#15803d' : '#b91c1c', color: '#fff' }}>
                {gravando ? 'Gravando...' : decidindo.status === 'L' ? 'Confirmar aprovação' : 'Confirmar reprovação'}
              </button>
            </div>
          </div>
        </div>
      )}

      {historico && (
        <div className="modal-overlay" style={{ zIndex: 1300 }} onClick={e => e.target === e.currentTarget && setHistorico(null)}>
          <div className="modal-box" style={{ maxWidth: 620 }}>
            <div className="modal-header"><h2>Histórico de aprovação — ordem {historico.ordem.numero}</h2><button className="modal-fechar" onClick={() => setHistorico(null)}>✕</button></div>
            <div className="modal-corpo">
              {historico.itens.length === 0 && <p style={{ color: '#94a3b8', fontSize: '0.84rem' }}>Sem histórico (ordem anterior à aprovação no web).</p>}
              {historico.itens.map((h, i) => (
                <div key={i} style={{ display: 'grid', gridTemplateColumns: '130px 150px 1fr', gap: 8, padding: '6px 0', borderBottom: '1px solid #f1f5f9', fontSize: '0.82rem' }}>
                  <span style={{ fontWeight: 800, color: (COR[h.status] ?? COR.A).cor }}>{h.rotulo}</span>
                  <span>{h.data_hora.split(' ')[0].split('-').reverse().join('/')} {h.data_hora.split(' ')[1]} · {h.usuario || '—'}</span>
                  <span style={{ color: '#475569' }}>{h.observacao || ''}</span>
                </div>
              ))}
            </div>
            <div className="modal-footer"><button className="btn btn-secondary" onClick={() => setHistorico(null)}>Fechar</button></div>
          </div>
        </div>
      )}
    </div>
  )
}
